/**
 * useFolderManagement Hook
 * Hook for folder operations
 */

import { useState, useCallback, useEffect } from 'react';
import { useFileServices } from '@actschurch/ccms-sdk';
import { FolderInfo, FolderTreeNode, CreateFolderRequest } from '../types';
import { toast } from '@/components/ui/providers/AlertProvider';

export interface UseFolderManagementReturn {
  // State
  folders: FolderInfo[];
  folderTree: FolderTreeNode[];
  loading: boolean;
  error: string | null;

  // Actions
  fetchFolders: (parentPath?: string) => Promise<void>;
  getFolderTree: (domain?: string, recordId?: string) => Promise<void>;
  createFolder: (data: CreateFolderRequest) => Promise<FolderInfo | null>;
  renameFolder: (id: string, newName: string) => Promise<void>;
  moveFolder: (id: string, newParentPath: string) => Promise<void>;
  deleteFolder: (id: string, deleteContents?: boolean) => Promise<void>;
  refreshFolders: () => Promise<void>;
}

export const useFolderManagement = (
  domain?: string,
  recordId?: string,
  initialParentPath?: string
): UseFolderManagementReturn => {
  const { folder: folderService } = useFileServices();

  // State
  const [folders, setFolders] = useState<FolderInfo[]>([]);
  const [folderTree, setFolderTree] = useState<FolderTreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch folders
  const fetchFolders = useCallback(
    async (parentPath?: string) => {
      setLoading(true);
      setError(null);

      try {
        const result = await folderService.list({
          domain,
          recordId,
          parentPath: parentPath ?? initialParentPath,
          recursive: false,
        });

        setFolders(result);
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to fetch folders';
        setError(errorMessage);
        toast.error('Error', errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [folderService, domain, recordId, initialParentPath]
  );

  // Get folder tree
  const getFolderTree = useCallback(
    async (treeDomain?: string, treeRecordId?: string) => {
      setLoading(true);
      setError(null);

      try {
        const result = await folderService.getTree({
          domain: treeDomain ?? domain,
          recordId: treeRecordId ?? recordId,
        });

        setFolderTree(result);
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to fetch folder tree';
        setError(errorMessage);
        toast.error('Error', errorMessage);
      } finally{
        setLoading(false);
      }
    },
    [folderService, domain, recordId]
  );

  // Create folder
  const createFolder = useCallback(
    async (data: CreateFolderRequest): Promise<FolderInfo | null> => {
      try {
        const result = await folderService.create(data);

        toast.success('Success', `Folder "${data.name}" created successfully`);

        await fetchFolders(data.parentPath);
        await getFolderTree();

        return result;
      } catch (err: any) {
        toast.error('Error', err.message || 'Failed to create folder');
        return null;
      }
    },
    [folderService, fetchFolders, getFolderTree]
  );

  // Rename folder
  const renameFolder = useCallback(
    async (id: string, newName: string) => {
      try {
        await folderService.rename(id, { newName });

        toast.success('Success', 'Folder renamed successfully');

        await fetchFolders();
        await getFolderTree();
      } catch (err: any) {
        toast.error('Error', err.message || 'Failed to rename folder');
      }
    },
    [folderService, fetchFolders, getFolderTree]
  );

  // Move folder
  const moveFolder = useCallback(
    async (id: string, newParentPath: string) => {
      try {
        await folderService.move(id, { newParentPath });

        toast.success('Success', 'Folder moved successfully');

        await fetchFolders();
        await getFolderTree();
      } catch (err: any) {
        toast.error('Error', err.message || 'Failed to move folder');
      }
    },
    [folderService, fetchFolders, getFolderTree]
  );

  // Delete folder
  const deleteFolder = useCallback(
    async (id: string, deleteContents: boolean = false) => {
      try {
        await folderService.delete(id, deleteContents);

        toast.success('Success', 'Folder deleted successfully');

        await fetchFolders();
        await getFolderTree();
      } catch (err: any) {
        toast.error('Error', err.message || 'Failed to delete folder');
      }
    },
    [folderService, fetchFolders, getFolderTree]
  );

  // Refresh folders
  const refreshFolders = useCallback(async () => {
    await fetchFolders();
    await getFolderTree();
  }, [fetchFolders, getFolderTree]);

  // Fetch folders on mount
  useEffect(() => {
    fetchFolders();
    getFolderTree();
  }, [fetchFolders, getFolderTree]);

  return {
    // State
    folders,
    folderTree,
    loading,
    error,

    // Actions
    fetchFolders,
    getFolderTree,
    createFolder,
    renameFolder,
    moveFolder,
    deleteFolder,
    refreshFolders,
  };
};
